import React, { useEffect, useState } from 'react';
import BaseModal from './BaseModal';
import Button from '../common/Button';
import { ethers } from 'ethers';

// Helper function to format number for display
const formatNumber = (value) => {
  if (!value || value === 'NaN' || isNaN(value)) return '0';
  
  try {
    const strValue = value.toString();
    
    // Check if it's exactly 1 wei (0.000000000000000001)
    if (strValue === '0.000000000000000001') {
      return '1';
    }
    
    // Check if it's a scientific notation
    if (strValue.includes('e-')) {
      const [base, exponent] = strValue.split('e-');
      const exp = parseInt(exponent);
      const formatted = '0.' + '0'.repeat(exp - 1) + base.replace('.', '');
      // Check if the formatted result is exactly 1 wei
      if (formatted === '0.000000000000000001') {
        return '1';
      }
      return formatted;
    }
    
    // If it's a very small decimal, return it as is
    const numValue = parseFloat(strValue);
    if (numValue === 0) return '0';
    
    // If it's less than 0.000001, show full precision
    if (numValue < 0.000001) {
      const formatted = strValue.replace(/\.?0+$/, '') || '0';
      // Check if the formatted result is exactly 1 wei
      if (formatted === '0.000000000000000001') {
        return '1';
      }
      return formatted;
    }
    
    // For larger numbers, show up to 6 decimal places
    if (numValue < 1) {
      return numValue.toFixed(6).replace(/\.?0+$/, '');
    }
    
    // For whole numbers
    if (Number.isInteger(numValue)) {
      return numValue.toString();
    }
    
    // For other numbers, show up to 4 decimal places
    return numValue.toFixed(4).replace(/\.?0+$/, '');
  } catch (error) {
    console.error('Error formatting number:', error, 'Value:', value);
    return '0';
  }
};

// Helper to convert ether string to wei BigInt
const etherToWei = (etherString) => {
  if (!etherString) return 0n;
  
  try {
    // Handle the special case where we want to claim 1 wei
    if (etherString === '1') {
      return 1n;
    }
    return ethers.parseEther(etherString);
  } catch (error) {
    console.error('Error converting ether to wei:', error, 'Value:', etherString);
    return 0n;
  }
};

// Helper to check if value is effectively zero
const isEffectivelyZero = (value) => {
  try {
    const numValue = parseFloat(value);
    return numValue < 0.000000000000000001; // Less than 1 wei (1e-18)
  } catch {
    return false;
  }
};

const LegacyClaimsModal = ({
  isOpen,
  onClose,
  legacyInfo,
  legacyClaimPrice,
  etcPowBalance,
  onClaim,
  onFetchLegacyInfo,
  onFetchClaimPrice,
  onFetchBalance
}) => {
  const [claimAmount, setClaimAmount] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  

  // Fetch all data when modal opens
  useEffect(() => {
    if (isOpen) {
      const fetchData = async () => {
        setIsLoading(true);
        try {
          const legacyResult = await onFetchLegacyInfo();
          
          const priceResult = await onFetchClaimPrice();
          const balanceResult = await onFetchBalance();
        } catch (error) {
          console.error('Error fetching modal data:', error);
        } finally {
          setIsLoading(false);
        }
      };
      fetchData();
    }
  }, [isOpen]); // Remove functions from dependencies to prevent infinite loops

  const handleSubmit = async () => {
    console.log('handleSubmit called with claimAmount:', claimAmount);
    
    if (!claimAmount || parseFloat(claimAmount) <= 0) {
      console.error('Invalid claim amount:', claimAmount);
      return;
    }

    try {
      setIsLoading(true);
      
      const amountNum = parseInt(claimAmount);

      if (isNaN(amountNum) || amountNum <= 0) {
        console.error('Invalid claim amount:', claimAmount);
        return;
      }

      // For legacy claims, we pass the raw number of licenses (not wei conversion)
      await onClaim(amountNum);
      
      setClaimAmount('');
      
      // Refresh all data after a successful claim
      if (onFetchLegacyInfo && onFetchClaimPrice && onFetchBalance) {
        await Promise.all([
          onFetchLegacyInfo(),
          onFetchClaimPrice(),
          onFetchBalance()
        ]);
      }
    } catch (error) {
      console.error('Error claiming tokens:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Parse the remaining amount and the formatted amount for comparison
  const remainingAmount = parseFloat(legacyInfo?.remaining || '0');
  const formattedRemaining = formatNumber(legacyInfo?.remaining || '0');
  const canClaim = remainingAmount > 0 && !isEffectivelyZero(remainingAmount);

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={() => {
        setClaimAmount('');
        onClose();
      }}
      title="Legacy Claims"
      subtitle={isLoading ? 'Loading legacy information...' : 'Claim your entitled licenses'}
      icon="🎫"
      footer={
        <div style={{
          display: 'flex',
          gap: '16px'
        }}>
          <Button
            variant="secondary"
            onClick={onClose}
            style={{
              flex: 1
            }}
          >
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={handleSubmit}
            disabled={!claimAmount || 
                     parseFloat(claimAmount) <= 0 || 
                     (claimAmount !== formattedRemaining && parseFloat(claimAmount) > remainingAmount) ||
                     !canClaim ||
                     isLoading}
            style={{
              flex: 1,
              opacity: isLoading ? 0.7 : 1,
              cursor: isLoading ? 'not-allowed' : 'pointer'
            }}
          >
            <span style={{
              marginRight: '8px',
              display: 'inline-flex',
              alignItems: 'center'
            }}>
              {isLoading ? (
                <span style={{
                  display: 'inline-block',
                  width: '16px',
                  height: '16px',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderRadius: '50%',
                  borderTopColor: 'white',
                  animation: 'spin 1s linear infinite',
                  marginRight: '8px'
                }} />
              ) : '🎫'}
              {isLoading ? 'Processing...' : 'Claim'}
            </span>
          </Button>
        </div>
      }
    >
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px'
      }}>
        {/* Stats Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '16px'
        }}>
          <div style={{
            backgroundColor: 'rgba(251, 146, 60, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '16px',
            border: '1px solid rgba(251, 146, 60, 0.3)',
            padding: '24px',
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(251, 146, 60, 0.1)'
          }}>
            <div style={{
              width: '56px',
              height: '56px',
              background: 'linear-gradient(135deg, #f59e0b, #d97706)',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              boxShadow: '0 8px 32px rgba(251, 146, 60, 0.2)',
              border: '1px solid rgba(251, 146, 60, 0.4)'
            }}>
              <span style={{
                fontSize: '32px',
                filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
              }}>📋</span>
            </div>
            <p style={{
              fontSize: '12px',
              fontWeight: '600',
              color: 'rgba(255, 255, 255, 0.8)',
              margin: '0',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
            }}>ENTITLED</p>
            <p style={{
              fontSize: '28px',
              fontWeight: '700',
              color: 'white',
              margin: '4px 0 0',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
              wordBreak: 'break-word',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
              padding: '0 8px'
            }} title={legacyInfo?.entitled || '0'}>
              {isLoading ? '...' : formatNumber(legacyInfo?.entitled || '0')}
            </p>
          </div>
          <div style={{
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '16px',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            padding: '24px',
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(16, 185, 129, 0.1)'
          }}>
            <div style={{
              width: '56px',
              height: '56px',
              background: 'linear-gradient(135deg, #10b981, #059669)',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              boxShadow: '0 8px 32px rgba(16, 185, 129, 0.2)',
              border: '1px solid rgba(16, 185, 129, 0.4)'
            }}>
              <span style={{
                fontSize: '32px',
                filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
              }}>✅</span>
            </div>
            <p style={{
              fontSize: '12px',
              fontWeight: '600',
              color: 'rgba(255, 255, 255, 0.8)',
              margin: '0',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
            }}>CLAIMED</p>
            <p style={{
              fontSize: '28px',
              fontWeight: '700',
              color: 'white',
              margin: '4px 0 0',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
              wordBreak: 'break-word',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
              padding: '0 8px'
            }} title={legacyInfo?.claimed || '0'}>
              {isLoading ? '...' : formatNumber(legacyInfo?.claimed || '0')}
            </p>
          </div>
          <div style={{
            backgroundColor: 'rgba(251, 146, 60, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '16px',
            border: '1px solid rgba(251, 146, 60, 0.3)',
            padding: '24px',
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(251, 146, 60, 0.1)'
          }}>
            <div style={{
              width: '56px',
              height: '56px',
              background: 'linear-gradient(135deg, #f59e0b, #d97706)',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              boxShadow: '0 8px 32px rgba(251, 146, 60, 0.2)',
              border: '1px solid rgba(251, 146, 60, 0.4)'
            }}>
              <span style={{
                fontSize: '32px',
                filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
              }}>🎯</span>
            </div>
            <p style={{
              fontSize: '12px',
              fontWeight: '600',
              color: 'rgba(255, 255, 255, 0.8)',
              margin: '0',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
            }}>REMAINING</p>
            <p style={{
              fontSize: '28px',
              fontWeight: '700',
              color: 'white',
              margin: '4px 0 0',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
              wordBreak: 'break-word',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
              padding: '0 8px'
            }} title={legacyInfo?.remaining || '0'}>
              {isLoading ? '...' : formatNumber(legacyInfo?.remaining || '0')}
            </p>
          </div>
        </div>

        {/* Claim Price Info */}
        <div style={{
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          padding: '20px',
          marginBottom: '24px',
          boxShadow: '0 8px 32px rgba(59, 130, 246, 0.1)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px'
          }}>
            <span style={{
              fontSize: '14px',
              fontWeight: '600',
              color: 'rgba(255, 255, 255, 0.9)'
            }}>
              💰 Claim Price (per license)
            </span>
            <span style={{
              fontSize: '18px',
              fontWeight: '700',
              color: '#3b82f6',
              textShadow: '0 2px 4px rgba(59, 130, 246, 0.3)'
            }}>
              {formatNumber(legacyClaimPrice)} ETCPOWv2
            </span>
          </div>
          <div style={{
            fontSize: '12px',
            color: 'rgba(255, 255, 255, 0.7)',
            lineHeight: '1.4'
          }}>
            You need to pay this amount in ETCPOWv2 to claim your legacy licenses
          </div>
          
          {/* Dynamic Total Cost */}
          {claimAmount && parseFloat(claimAmount) > 0 && (
            <div style={{
              marginTop: '12px',
              padding: '12px',
              backgroundColor: 'rgba(16, 185, 129, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(16, 185, 129, 0.3)'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <span style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: 'rgba(255, 255, 255, 0.9)'
                }}>
                  🧾 Total Cost for {formatNumber(claimAmount)} license(s)
                </span>
                <span style={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#10b981',
                  textShadow: '0 2px 4px rgba(16, 185, 129, 0.3)'
                }}>
                  {formatNumber(parseFloat(claimAmount) * parseFloat(legacyClaimPrice || '0'))} ETCPOWv2
                </span>
              </div>
              <div style={{
                fontSize: '11px',
                color: 'rgba(255, 255, 255, 0.6)',
                lineHeight: '1.3'
              }}>
                {parseFloat(claimAmount) * parseFloat(legacyClaimPrice || '0') > parseFloat(etcPowBalance || '0') 
                  ? `⚠️ Insufficient balance. You need ${formatNumber((parseFloat(claimAmount) * parseFloat(legacyClaimPrice || '0')) - parseFloat(etcPowBalance || '0'))} more ETCPOWv2`
                  : `✅ Sufficient balance for this claim`
                }
              </div>
            </div>
          )}
          
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '12px',
            padding: '12px',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '8px',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <span style={{
              fontSize: '14px',
              fontWeight: '600',
              color: 'rgba(255, 255, 255, 0.9)'
            }}>
              💳 Your Balance
            </span>
            <span style={{
              fontSize: '16px',
              fontWeight: '700',
              color: '#10b981',
              textShadow: '0 2px 4px rgba(16, 185, 129, 0.3)'
            }}>
              {formatNumber(etcPowBalance)} ETCPOWv2
            </span>
          </div>
        </div>

        {/* Claim Form */}
        <div style={{
          backgroundColor: 'rgba(55, 65, 81, 0.1)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          padding: '24px',
          boxShadow: '0 8px 32px rgba(55, 65, 81, 0.1)'
        }}>
          <label style={{
            display: 'block',
            fontWeight: '500',
            color: 'white',
            marginBottom: '8px',
            fontSize: '14px',
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
          }}>
            Amount to Claim
          </label>
          <input
            type="number"
            value={claimAmount}
            onChange={(e) => {
              const value = e.target.value;
              // Ensure we get a clean integer, prevent scientific notation
              if (value === '' || value === '0') {
                setClaimAmount('0');
              } else {
                // Convert to number and back to string to remove scientific notation
                const numValue = Number(value);
                if (!isNaN(numValue) && numValue >= 0) {
                  setClaimAmount(Math.floor(numValue).toString());
                }
              }
            }}
            placeholder="0"
            min="0"
            max={remainingAmount}
            step="1"
            style={{
              width: '100%',
              fontSize: '16px',
              padding: '12px 16px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              borderRadius: '8px',
              transition: 'all 0.2s ease',
              outline: 'none',
              margin: 0
            }}
            disabled={isLoading || !canClaim}
          />
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '8px'
          }}>
            <p style={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '14px',
              margin: 0,
            }}>
              Available: <span style={{
                fontWeight: '600',
                color: canClaim ? '#fb923c' : 'rgba(255, 255, 255, 0.5)',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
              }}>
                {isLoading ? '...' : `${formatNumber(legacyInfo?.remaining || '0')} licenses`}
              </span>
            </p>
            {canClaim && remainingAmount > 0 && (
              <button
                onClick={() => setClaimAmount(formatNumber(legacyInfo.remaining))}
                style={{
                  background: 'transparent',
                  border: '1px solid #fb923c',
                  color: '#fb923c',
                  padding: '4px 12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Max
              </button>
            )}
          </div>
        </div>

        {/* Expiration Warning */}
        <div style={{
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          border: '1px solid rgba(245, 158, 11, 0.3)',
          padding: window.innerWidth < 640 ? '16px' : '24px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '16px',
          boxShadow: '0 8px 32px rgba(245, 158, 11, 0.1)'
        }}>
          <div style={{
            marginTop: '2px',
            height: '40px',
            width: '40px',
            backgroundColor: '#f59e0b',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <span style={{
              fontSize: '16px',
              fontWeight: 'bold',
              color: 'white',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)'
            }}>⏰</span>
          </div>
          <div>
            <p style={{
              color: 'white',
              fontWeight: '600',
              fontSize: '16px',
              margin: '0 0 12px 0',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
            }}>License Expiration</p>
            <p style={{
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '14px',
              margin: '0',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
              lineHeight: '1.5'
            }}>
              All claimed legacy licenses have a <span style={{ color: '#fbbf24', fontWeight: '600' }}>6-month validity period</span> from the date of claiming.
              After expiration, licenses cannot be used or listed for sale.
            </p>
          </div>
        </div>

        {/* Info Box */}
        <div style={{
          backgroundColor: 'rgba(251, 146, 60, 0.1)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          border: '1px solid rgba(251, 146, 60, 0.3)',
          padding: '24px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '16px',
          boxShadow: '0 8px 32px rgba(251, 146, 60, 0.05)'
        }}>
          <div style={{
            marginTop: '2px',
            height: '40px',
            width: '40px',
            backgroundColor: '#f59e0b',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: '0 4px 12px rgba(251, 146, 60, 0.3)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <span style={{
              fontSize: '16px',
              fontWeight: 'bold',
              color: 'white',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)'
            }}>!</span>
          </div>
          <div>
            <p style={{
              color: 'white',
              fontWeight: '600',
              fontSize: '16px',
              margin: '0 0 8px 0',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
            }}>Important</p>
            <ul style={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '14px',
              margin: '0',
              paddingLeft: '0',
              listStyle: 'none'
            }}>
              <li style={{
                marginBottom: '4px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px'
              }}>
                <span style={{
                  color: '#fb923c',
                  fontSize: '16px'
                }}>•</span>
                <span>Values shown are in token units (not WEI)</span>
              </li>
              <li style={{
                marginBottom: '4px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px'
              }}>
                <span style={{
                  color: '#fb923c',
                  fontSize: '16px'
                }}>•</span>
                <span>Claims are final</span>
              </li>
              <li style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px'
              }}>
                <span style={{
                  color: '#fb923c',
                  fontSize: '16px'
                }}>•</span>
                <span>Gas fees apply</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </BaseModal>
  );
};

export default LegacyClaimsModal;